import { PRODUCT_SIZES, PRODUCT_TAGS, PRODUCT_TYPES } from '~/lib/constants'
import {
  productMetafields,
  productPackMetafields,
} from '~/lib/shopify/constants/metafields'
import { getProductFromShopify } from '~/lib/shopify/products'
import { metafieldsToObject } from '~/lib/shopify/utils/metafield-to-object'

import { GID, UnlikelyProduct } from '@unlikelystudio/commerce-connector'
import { Metafield } from '@unlikelystudio/ecommerce-typed/types/shopify-admin'
import { Product } from '@unlikelystudio/react-ecommerce-hooks'

import { getShopifyProductIdFromStoryblokData } from '~/utils/get-shopify-id-from-storyblok-data'
import { objectValues } from '~/utils/object-values'
import {
  getProductSizeFromTags,
  getProductTypeFromTags,
} from '~/utils/product-customizer'

import { SerializedProduct } from '~/data/product-card-data/types'
import { serializeByCharlotPackProducts } from '~/data/serialize-bycharlot-pack'
import {
  getByCharlotCustomizationIds,
  serializeCustomizationProducts,
} from '~/data/serialize-customization-products'
import { serializeCustomizerProductVariants } from '~/data/serialize-customizer-product-variants'
import { serializeLabelCustomizationProducts } from '~/data/serialize-label-customization-products'
import serializePrice from '~/data/serialize-price'
import serializeVariant from '~/data/serialize-variant'

export interface ByCharlotProduct extends Product {
  refill_product?: Metafield
  is_simple_product_pre_order?: Metafield
  pack_products_1?: Metafield
  pack_products_2?: Metafield
  pack_products_3?: Metafield
}

export async function serializeByCharlotCustomizable(product) {
  const productType = getProductTypeFromTags(product?.tags)
  const excludeMetalPot =
    product?.tags?.includes(PRODUCT_TAGS.OOS_METAL) ?? false
  const excludeCeramicPot =
    product?.tags?.includes(PRODUCT_TAGS.OOS_CERAMIC) ?? false
  const size = getProductSizeFromTags(product?.tags ?? []) ?? null

  const hasDefaultLabelCustomization =
    productType === PRODUCT_TYPES.CANDLE ||
    productType === PRODUCT_TYPES.POMANDER ||
    productType === PRODUCT_TYPES.CANDLEV2 ||
    productType === PRODUCT_TYPES.PLANT ||
    productType === PRODUCT_TYPES.INFUSION ||
    productType === PRODUCT_TYPES.HERBARIUM ||
    productType === PRODUCT_TYPES.FRAGRANCE

  const disablePreview =
    productType === PRODUCT_TYPES.CANDLE && size === PRODUCT_SIZES.M

  return {
    hasDefaultLabelCustomization,
    disablePreview,
    productType,
    excludeMetalPot,
    excludeCeramicPot,
    size,
  }
}

export async function getByCharlotCustomizationProducts(
  product: UnlikelyProduct,
  type,
  locale,
) {
  const requiredCustomizationIds = getByCharlotCustomizationIds(product, true)
  const customizationIds = getByCharlotCustomizationIds(product, false)

  // get Required Customization Product (pot for plant / candle / infusion, frame for herbarium, puck for fragrance...)
  const requiredCustomizationProduct =
    (
      await serializeCustomizationProducts(
        { ...product, type },
        locale,
        true,
        requiredCustomizationIds,
      )
    )?.[0] ?? null

  // get Customization products
  const customizationProducts =
    (await serializeCustomizationProducts(
      { ...product, type },
      locale,
      false,
      customizationIds,
    )) ?? []

  // get label Customization products
  const labelCustomizationProducts =
    (await serializeLabelCustomizationProducts(product, locale)) ?? []

  return {
    labelCustomizationProducts,
    requiredCustomizationProduct,
    customizationProducts: [
      requiredCustomizationProduct,
      ...customizationProducts,
    ]?.filter(Boolean),
  }
}

/**
 * Shopify Storefront product data serializer
 * @param product
 * @param locale
 * @param mainProductImages
 * @returns serialized storefront shopify data
 */
export async function serializeShopifyStorefrontData(
  product: UnlikelyProduct,
  locale,
  inProductHeader,
  bundleMainProduct = null,
): Promise<any> {
  let upsales = null

  const metafields = metafieldsToObject(product?.metafields, {
    ...productMetafields,
  })
  const packsMetafields = metafieldsToObject(product?.metafields, {
    ...productPackMetafields,
  })

  const productType = getProductTypeFromTags(product?.tags ?? [])
  const isProductsPack = product?.tags?.includes(PRODUCT_TAGS.PACK) ?? false
  const isSimpleProductPreOrder = metafields?.isSimpleProductPreOrder === 'true'
  const packTabsIds = objectValues(packsMetafields)
    ?.map((metafield) => {
      try {
        return JSON.parse(metafield ?? '')
      } catch {
        return null
      }
    })
    .filter(Boolean)

  // We will only fetch the first product of each tab to lighten the page payload
  const packIdsToFetch = packTabsIds.map((tab) => [tab[0]])

  const packProducts =
    isProductsPack && inProductHeader
      ? await serializeByCharlotPackProducts(packIdsToFetch, locale)
      : null

  /* ByCharlot customization variables */
  const isByCharlotCustomizationProduct = Boolean(
    (inProductHeader &&
      bundleMainProduct?.tags?.includes(PRODUCT_TAGS.CUSTOMIZE)) ||
      productType === PRODUCT_TYPES.ENGRAVING ||
      productType === PRODUCT_TYPES.LABEL,
  )

  // if product has a customize tag and no mainProduct is provided, we are serializing the mainProduct of a bycharlot bundle
  const isByCharlotBundleMainProduct = Boolean(
    inProductHeader &&
      !isProductsPack &&
      product?.tags?.includes(PRODUCT_TAGS.CUSTOMIZE) &&
      !bundleMainProduct,
  )

  const byCharlotCustomizableProperties: SerializedProduct =
    isByCharlotCustomizationProduct || isByCharlotBundleMainProduct
      ? await serializeByCharlotCustomizable(product)
      : {}

  const byCharlotCustomizationProducts = isByCharlotBundleMainProduct
    ? await getByCharlotCustomizationProducts(
        product,
        byCharlotCustomizableProperties?.productType,
        locale,
      )
    : {}

  const serializedVariants = product?.variants
    ?.map((variant) => {
      if (Boolean(variant) === false) {
        console.error({ product, variants: product.variants })
      }
      return serializeVariant(variant)
    })
    .filter(
      (variant) =>
        Boolean(variant) &&
        // We need to hide unavailable variants for engravings on bycharlot
        (productType !== PRODUCT_TYPES.ENGRAVING ||
          (variant.availableForSale &&
            productType === PRODUCT_TYPES.ENGRAVING)),
    )

  const variants = isByCharlotCustomizationProduct
    ? serializeCustomizerProductVariants(
        serializedVariants,
        product,
        bundleMainProduct,
      )
    : serializedVariants ?? []

  // Order variants by ascending price with availableForSale in non customization products
  // Order based on parent product images order for customization products
  const orderedAvailableVariants = isByCharlotCustomizationProduct
    ? [...variants]?.sort((a, b) => {
        return a.variantIndex - b.variantIndex
      })
    : [...variants]?.sort((a, b) => {
        if (a.availableForSale && !b.availableForSale) return -1
        return a.price?.amount - b.price?.amount
      })

  // set the first as default variant
  const defaultVariant = orderedAvailableVariants?.[0]
  // Order variants by ascending price to find the cheapest variant
  const cheapestVariant = [...variants]?.sort((a, b) => {
    return a.price?.amount - b.price?.amount
  })?.[0]
  const { currencyCode, amount } = cheapestVariant?.price ?? {}
  const price = serializePrice(locale, currencyCode, amount)
  const collections = product?.collections?.map((item) => item?.id) ?? []

  return {
    shopifyId: product?.id ?? null,
    price,
    isSimpleProductPreOrder: isSimpleProductPreOrder ?? false,
    collections,
    featuredImage: product?.featuredImage?.url ?? null,
    compareAtPrice: cheapestVariant?.compareAtPrice ?? null,
    priceFrom: cheapestVariant?.price ?? null,
    availableForSale: product?.availableForSale ?? false,
    variants: orderedAvailableVariants ?? null,
    defaultVariant: defaultVariant ?? null,
    tags: product?.tags ?? null,
    ...byCharlotCustomizableProperties,
    ...byCharlotCustomizationProducts,
    byCharlotCustomizable: isByCharlotBundleMainProduct,
    productType,
    packProducts,
    packTabsIds,
  }
}

/**
 * Fetch & serialize storefront Shopify data
 * @param data
 * @param locale
 * @param inProductHeader
 * @param shouldFetchCollections
 * @param shouldFetchImages
 * @returns serialized shopify data
 */
export async function fetchShopifyProductCard({
  data,
  locale,
  inProductHeader,
  shouldFetchCollections,
  shouldFetchImages,
  shouldFetchPackMetafields = false,
}) {
  let shopifyProductId: string
  if (typeof data !== 'string') {
    // Retrieve the data from storyblok content
    shopifyProductId = getShopifyProductIdFromStoryblokData(data)
  } else {
    shopifyProductId = data
  }

  // Fetch product from shopify
  const shopifyProduct = shopifyProductId
    ? await getProductFromShopify({
        locale,
        id: shopifyProductId as GID,
        shouldFetchCollections,
        shouldFetchImages,
        shouldFetchPackMetafields,
      })
    : null

  if (!shopifyProduct) return null
  // Return serialized Shopify product data
  return serializeShopifyStorefrontData(shopifyProduct, locale, inProductHeader)
}
